import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext.jsx";
import { useOktaSession } from "../../okta/OktaSessionContext.jsx";
import { getRequirementsGateForContext } from "../../utils/oktaRequirements.js";

/**
 * Legacy SSWS token + org URL for proactive agent interview (Dashboard).
 * Full reconnect form only when there is no token or Okta returned 401.
 */
export default function SswsTenantStrip({ currentContext, disabled = false }) {
  const { validateSswsToken, refreshCapabilities, currentCapabilities } = useAuth();
  const { setSession, orgUrl: sessionOrgUrl, apiToken: sessionApiToken, oktaConnected, sswsUnauthorized } =
    useOktaSession();
  const [orgUrl, setOrgUrl] = useState(sessionOrgUrl || "");
  const [token, setToken] = useState(sessionApiToken || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setOrgUrl(sessionOrgUrl || "");
  }, [sessionOrgUrl]);

  useEffect(() => {
    setToken(sessionApiToken || "");
  }, [sessionApiToken]);

  if (oktaConnected && !sswsUnauthorized) {
    return null;
  }

  const req = getRequirementsGateForContext(currentContext);

  async function onValidate() {
    setBusy(true);
    setError("");
    try {
      await validateSswsToken(orgUrl, token);
      const trimmedUrl = orgUrl.trim();
      setSession({ orgUrl: trimmedUrl, apiToken: token.trim() });
    } catch (e) {
      if (e?.status === 401) {
        setError(e?.message || "Okta rejected this token (401). Paste a new SSWS API token.");
      } else {
        setError(e?.message || "Validation failed");
      }
    } finally {
      setBusy(false);
    }
  }

  const verified = Boolean(currentCapabilities?.userId && currentCapabilities?.tabAccess);

  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#020617]/90 px-3 py-4">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Legacy SSWS (tenant)</p>
      <p className="mt-1 text-[11px] leading-snug text-slate-400">{req}</p>
      {sswsUnauthorized ? (
        <p className="mt-2 text-[11px] text-amber-400/95">
          Okta returned 401 Unauthorized — paste a valid SSWS API token for this org.
        </p>
      ) : oktaConnected && !verified ? (
        <p className="mt-2 text-[11px] text-slate-400">
          Stored credentials did not validate. Update the token or org URL and choose Validate.
        </p>
      ) : null}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <label className="text-[10px] text-slate-500">Okta Org URL</label>
          <input
            value={orgUrl}
            onChange={(e) => setOrgUrl(e.target.value)}
            placeholder="https://dev-123.okta.com"
            disabled={disabled}
            className="mt-0.5 w-full rounded-lg border border-white/10 bg-[#050810] px-2 py-1.5 text-xs text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
            autoComplete="url"
          />
        </div>
        <div>
          <label className="text-[10px] text-slate-500">API token (SSWS)</label>
          <input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Paste token"
            type="password"
            className="mt-0.5 w-full rounded-lg border border-white/10 bg-[#050810] px-2 py-1.5 text-xs font-mono text-slate-200"
            autoComplete="off"
          />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onValidate}
          disabled={disabled || busy || !orgUrl.trim() || !token.trim()}
          className="rounded-lg bg-[rgb(var(--idaxis-accent-primary-rgb)/0.9)] px-3 py-1.5 text-xs font-semibold text-[var(--idaxis-bg-primary)] disabled:opacity-40"
        >
          {busy ? "…" : "Validate token"}
        </button>
        <button
          type="button"
          onClick={() => refreshCapabilities()}
          disabled={disabled}
          className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-400 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Refresh status
        </button>
      </div>
      {error ? <p className="mt-2 text-xs text-rose-400">{error}</p> : null}
    </div>
  );
}
